import {IAddressData, IPaymentData, PaymentFactory} from "@connox/typescript-library";
import {captureException} from "@sentry/browser";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import "../../../lib/GenericStyles.scss";
import {getShopId} from "../../../lib/getShopId";
import AmazonPayWalletWidget from "../../../lib/payments/AmazonPayWalletWidget";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import window from "../../../lib/WindowExtended";
import LineLoader from "../../../utils/loader/LineLoader";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import "./Payment.scss";
import PaymentRow from "./PaymentRow";

interface IBirthdayPickerContext {
  dateOfBirthRef: React.MutableRefObject<string | null>;
}

const birthdayPickerContext = React.createContext<IBirthdayPickerContext | null>(null);

export const useBirthdayPickerContext = (): IBirthdayPickerContext => {
  const context = React.useContext(birthdayPickerContext);
  if (!context) {
    throw new Error("not into component");
  }

  return context;
};

const Payment: React.FC<IIsVisible> = (props) => {
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation("checkout");
  const [paymentOptions, setPaymentOptions] = useState<IPaymentData[]>([]);
  const controllerId = PaymentController.getPaymentId() || checkout.paymentId;
  const tempPaymentModule = controllerId ? PaymentController.createPayment(checkout, controllerId) : null;
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(tempPaymentModule);
  const isContinueBlocked = !checkout.paymentId || checkout.paymentId === Payments.CreditCardBraintree;
  const buttonText = t("Zur Zusammenfassung");
  const [requestState, setRequestState] = useState<{
    buttonType: ButtonType;
    buttonValue: string | JSX.Element;
    errors: string[];
    failedPaymentId?: number;
  }>({
    buttonType: isContinueBlocked ? ButtonType.disabled : ButtonType.default,
    buttonValue: buttonText,
    errors: [],
  });
  // This ref is used in PaymentBirthdayPicker.tsx
  const dateOfBirthRef = useRef<string>(null);

  /**
   * request payment methods
   */
  const paymentIdsToBeFiltered = [100004, 100017, 100023, 100025, 100032];
  useEffect(() => {
    void PaymentFactory.getPaymentMethods(getShopId(), checkout.checkoutId).then((tempAvailable) => {
      const available = tempAvailable.filter((temp) => !paymentIdsToBeFiltered.includes(temp.paymentId));
      const preferredPaymentFirst = available.find(
        (payment) => payment.isCustomerPreferredPayment && payment.paymentAvailability.isAvailable,
      );
      if (preferredPaymentFirst && !checkout.paymentId) {
        setPaymentModule(PaymentController.createPayment(checkout, preferredPaymentFirst.paymentId));
      }
      setPaymentOptions(available);
    });
  }, [checkout.invoiceAddress, checkout.deliveryAddress]);

  /**
   * init inputs in payment row
   */
  useEffect(() => {
    if (!props.isVisible) {
      setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: []}));
    }

    if (!(paymentModule && props.isVisible)) {
      return;
    }
    paymentModule
      .onPaymentSelect()
      .then(() => setRequestState((prevState) => ({...prevState, buttonType: ButtonType.default})))
      .catch(() => {
        setRequestState((prevState) => ({
          ...prevState,
          buttonType: ButtonType.default,
          errors: [
            t(
              "Beim Initialisieren der Kreditkartenzahlung ist etwas schiefgegangen. Bitte versuchen Sie es später erneut oder wählen Sie eine andere Zahlungsart.",
            ),
          ],
        }));
      });
  }, [paymentModule, props.isVisible]);

  if (!props.isVisible) {
    return null;
  }

  if (!paymentOptions.length) {
    return (
      <div className="Shipping Mobile-Only-Friendly">
        <h1>{t("Zahlungsoptionen")}</h1>
        <ListLoader />
        <LineLoader speed={4} linePositionY={"2"} linePositionX={"0"} lineHeight={"3"} lineWidth={"50"} />
      </div>
    );
  }

  const preferredPayment = paymentOptions.find(
    (paymentOption) => paymentOption.isCustomerPreferredPayment && paymentOption.paymentAvailability.isAvailable,
  );
  const isButtonEnabled = PaymentController.getPaymentId() || !!preferredPayment;

  /**
   * payment clicked handler
   * @param pid
   */
  const onPaymentClicked = (pid: number) => {
    const oldPayment = PaymentController.getPayment();
    if (oldPayment && oldPayment.setAsDeselected) {
      oldPayment.setAsDeselected();
    }
    const newPayment = PaymentController.createPayment(checkout, pid);
    setPaymentModule(newPayment);
  };

  /**
   * Submit handler
   * @param evt
   */
  const submitPayment = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    const paymentId = PaymentController.getPaymentId();
    if (!(paymentId && paymentModule)) {
      return;
    }
    const submitErrors: string[] = [];
    setRequestState((prevState) => ({...prevState, buttonValue: <Spinner />}));
    let failedPaymentId: number | undefined;
    let addressWithBirthday: {delivery: IAddressData; invoice: IAddressData} | undefined;
    if (paymentOptions.find((available) => available.paymentId === paymentId && available.isScoringRequired)) {
      if (dateOfBirthRef.current === null || dateOfBirthRef.current === "") {
        submitErrors.push(t("Bitte geben Sie Ihren Geburtstag an"));
      }
      if (!checkout.invoiceAddress) {
        submitErrors.push(t("Sie müssen eine Rechnungsadresse angeben"));
      }
      if (submitErrors.length || !checkout.invoiceAddress) {
        setRequestState((prevState) => ({
          ...prevState,
          buttonValue: buttonText,
          errors: submitErrors,
        }));

        return;
      }
      const invoiceAddressWithBirthday = {...checkout.invoiceAddress, dateOfBirth: dateOfBirthRef.current};
      addressWithBirthday = {
        delivery: invoiceAddressWithBirthday,
        invoice: invoiceAddressWithBirthday,
      };
    }

    try {
      await paymentModule.onSubmitPayment();
      await checkout.put({
        paymentId,
        address: addressWithBirthday,
      });
    } catch (submitError) {
      /* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access */
      const {originalError} = submitError;
      if (originalError && originalError.response?.status === 400) {
        submitErrors.push(t("Die ausgewählte Zahlungsart steht nicht zur Verfügung."));
        failedPaymentId = paymentId;
      } else if (originalError) {
        const errorToPush = originalError.code || originalError.message;
        submitErrors.push(t(errorToPush, t(DEFAULT_ERROR_MESSAGE)));
      } else if (submitError.code || submitError.message) {
        submitErrors.push(t(submitError.code || submitError.message));
      }
      captureException(submitError);
    }
    setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: submitErrors, failedPaymentId}));
  };

  const onAmazonError = (errorString: string) => {
    setRequestState((prevState) => {
      const errorMessage = t(errorString, t(DEFAULT_ERROR_MESSAGE));

      return {...prevState, errors: [errorMessage]};
    });
  };

  if (checkout.paymentId === Payments.AmazonPay && window.orderReferenceId && getShopId() === 100001) {
    return <AmazonPayWalletWidget errors={requestState.errors} onError={onAmazonError} />;
  }

  return (
    <div className="Shipping Payment Mobile-Only-Friendly">
      <h1>{t("Zahlungsoptionen")}</h1>
      <CheckoutError>{requestState.errors}</CheckoutError>
      <form action="#">
        <div className="SimpleRadioList">
          <birthdayPickerContext.Provider value={{dateOfBirthRef}}>
            {paymentOptions.map((payment) => (
              <PaymentRow
                failedPaymentId={requestState.failedPaymentId}
                key={payment.paymentId}
                onPaymentClicked={onPaymentClicked}
                payment={payment}
              />
            ))}
          </birthdayPickerContext.Provider>
        </div>
        <CartDetails reduced />
        <Button
          styles={[ButtonStyles.wide]}
          action={submitPayment}
          type={
            isButtonEnabled && typeof requestState.buttonValue === "string"
              ? requestState.buttonType
              : ButtonType.disabled
          }
        >
          {requestState.buttonValue}
        </Button>
      </form>
    </div>
  );
};

export default Payment;
